import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { getWorkWiseAuthToken } from "../utils/helper";
import SplashScreen from "../components/SplashScreen/SplashScreen";
import { slideAction } from "../../src/store/slideSlice";
import { useDispatch, useSelector } from "react-redux";
import { workwiseServices } from "../components/WorkW-Api/workWiseApi";
import { getMergedCachedData } from "./documentCache";
import { isEffectivelyOffline } from "./helper";
import { isLocalPresentationRoute } from "./localPresentations";

const EXIT_DURATION = 550;
// Auth often resolves in a few hundred ms (and instantly when it is skipped),
// which made the loader flash on and straight back off. Hold it on screen for
// at least this long so the intro animation reads as intentional.
const MIN_VISIBLE_DURATION = 1500;

export const AuthenticateDocument = ({ setIsLoading, background = false }) => {

  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const isTemplateDocument = useSelector((state) => state.slide.isTemplateDocument);
  const [exiting, setExiting] = useState(false);
  const exitTimeoutRef = useRef(null);
  const holdTimeoutRef = useRef(null);
  // Wall clock of the first render, i.e. when the loader actually appeared.
  const shownAtRef = useRef(Date.now());

  useEffect(() => () => {
    clearTimeout(exitTimeoutRef.current);
    clearTimeout(holdTimeoutRef.current);
  }, []);

  // Plays the loader's fade-out before actually flipping the parent's
  // loading flag, so the document doesn't just snap into view. Background mode
  // draws no loader, so it hands over as soon as auth resolves.
  const finishLoading = () => {
    if (background) {
      setIsLoading(false);
      return;
    }

    const remainingHold = Math.max(
      0,
      MIN_VISIBLE_DURATION - (Date.now() - shownAtRef.current)
    );

    clearTimeout(holdTimeoutRef.current);
    holdTimeoutRef.current = setTimeout(() => {
      setExiting(true);
      exitTimeoutRef.current = setTimeout(() => setIsLoading(false), EXIT_DURATION);
    }, remainingHold);
  };

  const handleAuhenticate = async () => {
    try {
      setIsLoading(true);
      // Offline + local presentation: there is no server counterpart, so skip
      // the auth API entirely instead of redirecting to /no-internet.
      if (isEffectivelyOffline() && isLocalPresentationRoute()) {
        finishLoading();
        dispatch(slideAction.setIsAuthenticated(true));
        return;
      }
      if (!window.navigator.onLine) {
        // history.push('/no-internet');
        window.location.pathname = "/no-internet"

        return;
      }
      else {
        if (!!getWorkWiseAuthToken()) {
          const response = await workwiseServices.getDocumentById(id);
          if (response?.status == 200 && response.data?.data?.rightType !== 0 && response.data?.data) {
            dispatch(slideAction.setDocumentInformation(response.data.data));
            dispatch(slideAction.setIsAuthenticated(true));
            finishLoading();
          }
          if (response.data?.data?.rightType == 0) {
            // history.push("/unauthorized")
            window.location.pathname = "/unauthorized"
          }
          // history.push("/unauthorized")
        }
        else {
          // history.push("/unauthorized")
          window.location.pathname = "/unauthorized"
        }
      }
    } catch (error) {
      console.log({ error })
      if (error.response?.data?.responseCode === 1008) {
        if (isTemplateDocument) {
          dispatch(slideAction.setIsAuthenticated(true));
          finishLoading();
          return;
        }
        // history.push("/invalid-document")
        window.location.pathname = "/invalid-document"
      }
      else {
        // history.push("/unauthorized")
        window.location.pathname = "/unauthorized"
      }
    }
  }

  // In background mode, seed documentInfo + auth state from the cache up front so
  // the Header (avatar, members, share) renders fully while the auth API
  // re-validates behind the scenes. A cached entry only exists for a previously
  // authenticated user; if access was since revoked, the API call below redirects.
  useEffect(() => {
    if (background) {
      const merged = getMergedCachedData(id);
      if (merged?.documentInfo) {
        dispatch(slideAction.setDocumentInformation(merged.documentInfo));
        dispatch(slideAction.setIsAuthenticated(true));
      }
    }
  }, [background, id]);

  useEffect(() => {
    if (isTemplateDocument !== null) {
      handleAuhenticate();
    }
  }, [isTemplateDocument]);

  // Background mode renders nothing (no loader) — MainLayout is already on screen.
  if (background) return null;

  return <SplashScreen exiting={exiting} />
}